import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './ManagementReportsModal.css';
import { Report, requiredReportFields, createEmptyReport, optionalReportFields } from '../../models/Report';

interface ManagementReportsModalProps {
  isOpen: boolean;
  mode: 'add' | 'edit';
  form: Report;
  saving: boolean;
  formError: string | null;
  touched: { [key: string]: boolean };
  editReport?: any; // The report being edited (only used in edit mode)
  onClose: () => void;
  onSubmit: (e: React.FormEvent) => void;
  onFormChange: (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => void;
  isGuid: (value: string) => boolean;
}

type EditableField = Exclude<keyof Report, 'id'>;

const ManagementReportsModal: React.FC<ManagementReportsModalProps> = ({
  isOpen,
  mode,
  form,
  saving,
  formError,
  touched,
  onClose,
  onSubmit,
  onFormChange,
  isGuid,
}) => {
  const { t } = useTranslation();
  // ESC key closes modal
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const fields: ReadonlyArray<EditableField> = requiredReportFields;
  const optionalFields: ReadonlyArray<EditableField> = optionalReportFields;
  const placeholders: Record<string, string> = {
    displayname: t('management.displayName'),
    report: t('management.reportName'),
    category: t('management.category'),
    subcategory: t('management.subcategory'),
    workspacename: t('management.workspaceName'),
    workspaceid: t('management.workspaceId'),
    reportid: t('management.reportId'),
    azureSecurityGroupName: t('management.azureSecurityGroupName'),
    type: t('management.type'),
    refreshFrequency: t('management.refreshFrequency'),
  };


  const title = mode === 'add' ? t('management.addReport') : t('management.editReport');
  const buttonText = saving ? t('management.saving') : (mode === 'add' ? t('management.save') : t('management.update'));

  const baseForm = { ...createEmptyReport(), ...form };

  return (
    <div className="management-reports-modal__overlay">
      <div className="management-reports-modal__container">
        <h2 className="management-reports-modal__title">{title}</h2>
        <form onSubmit={onSubmit} className="management-reports-modal__form">
          {fields.map(field => {
            const isMissing = touched[field] && !baseForm[field as keyof Report];
            const fieldValue = baseForm[field as EditableField];
            const fieldValueStr = typeof fieldValue === 'string' ? fieldValue : '';
            const isInvalidGuid = (
              (field === 'workspaceid' || field === 'reportid') &&
              touched[field] && fieldValue &&
              !isGuid(fieldValueStr)
            );
            const inputClass = [
              'management-reports-modal__input',
              (isMissing || isInvalidGuid) ? 'management-reports-modal__input--error' : ''
            ].join(' ');
            return (
              <div key={field} className="management-reports-modal__field">
                <label htmlFor={field} className="management-reports-modal__label">
                  {placeholders[field]}
                </label>
                {field === 'type' ? (
                  <select
                    id={field}
                    name={field}
                    value={(baseForm[field as EditableField] as string) || 'Report'}
                    onChange={onFormChange}
                    className={inputClass}
                  >
                    <option value="Report">Report</option>
                    <option value="Paginated Report">Paginated Report</option>
                    <option value="Dashboard">Dashboard</option>
                  </select>
                ) : (
                  <input
                    id={field}
                    name={field}
                    value={baseForm[field as EditableField] as string || ''}
                    onChange={onFormChange}
                    placeholder={placeholders[field]}
                    className={inputClass}
                    autoComplete="off"
                  />
                )}
                {isInvalidGuid && (
                  <span className="management-reports-modal__error">{t('management.mustBeValidGuid')}</span>
                )}
              </div>
            );
          })}
          {optionalFields.map(field => {
            const inputClass = 'management-reports-modal__input';
            return (
              <div key={field} className="management-reports-modal__field">
                <label htmlFor={field} className="management-reports-modal__label">
                  {placeholders[field]}
                </label>
                {field === 'refreshFrequency' ? (
                  <input
                    id={field}
                    name={field}
                    type="number"
                    min="0"
                    step="1"
                    value={(baseForm[field as EditableField] as number) ?? 60}
                    onChange={(e) => {
                      const value = e.target.value;
                      // Parse the value - allow empty string which becomes 0
                      const numValue = value === '' ? 60 : (isNaN(parseInt(value, 10)) ? 60 : parseInt(value, 10));
                      const syntheticEvent = {
                        ...e,
                        target: {
                          ...e.target,
                          name: e.target.name,
                          value: numValue,
                        },
                      } as any;
                      onFormChange(syntheticEvent);
                    }}
                    placeholder="Milliseconds (0 = never refresh)"
                    className={inputClass}
                    autoComplete="off"
                  />
                ) : (
                  <input
                    id={field}
                    name={field}
                    value={baseForm[field as EditableField] as string || ''}
                    onChange={onFormChange}
                    placeholder={placeholders[field]}
                    className={inputClass}
                    autoComplete="off"
                  />
                )}
              </div>
            );
          })}
          {formError && <div className="management-reports-modal__error">{formError}</div>}
          <div className="management-reports-modal__actions">
            <button
              type="button"
              onClick={onClose}
              className="management-reports-modal__button management-reports-modal__button--cancel"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={saving}
              className="management-reports-modal__button management-reports-modal__button--submit"
            >
              {buttonText}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ManagementReportsModal; 